import { isHiddenCustomerCharge } from '../../utils/customerPricingVisibility';

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const roundMoney = (value) => Math.round(toNumber(value, 0) * 100) / 100;

const parseJson = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

const titleize = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();

const normalizeLine = (line = {}, fallbackType) => {
  const amount = roundMoney(
    line.amount ??
      line.value ??
      line.total ??
      line.fee_amount ??
      line.tax_amount
  );
  if (!amount) return null;

  return {
    id: line.id || null,
    label:
      line.display_name ||
      line.displayName ||
      line.rule_name ||
      line.ruleName ||
      line.name ||
      line.label ||
      titleize(fallbackType || 'Charge'),
    amount,
    chargeType: line.charge_type || line.chargeType || fallbackType || null,
    appliesOn: line.applies_on || line.appliesOn || line.applies_to || line.appliesTo || null,
  };
};

export const normalizeTaxFeeBreakdown = (raw, fallbackTotals = {}) => {
  const parsed = parseJson(raw) || {};
  const source = parsed.tax_fee_breakdown || parsed.taxes_and_fees || parsed;
  const feeSource = Array.isArray(source.fee_lines)
    ? source.fee_lines
    : Array.isArray(source.fees)
    ? source.fees
    : [];
  const taxSource = Array.isArray(source.tax_lines)
    ? source.tax_lines
    : Array.isArray(source.taxes)
    ? source.taxes
    : [];

  const rawFeeLines = feeSource.map((line) => normalizeLine(line, 'fee')).filter(Boolean);
  const rawTaxLines = taxSource.map((line) => normalizeLine(line, 'tax')).filter(Boolean);
  const hiddenFeeTotal = rawFeeLines.reduce(
    (sum, line) => (isHiddenCustomerCharge(line) ? sum + toNumber(line.amount) : sum),
    0
  );
  const hiddenTaxTotal = rawTaxLines.reduce(
    (sum, line) => (isHiddenCustomerCharge(line) ? sum + toNumber(line.amount) : sum),
    0
  );
  const feeLines = rawFeeLines.filter((line) => !isHiddenCustomerCharge(line));
  const taxLines = rawTaxLines.filter((line) => !isHiddenCustomerCharge(line));
  const feeTotal = Math.max(
    0,
    roundMoney(
      toNumber(
        source.fee_total ?? source.fees_total ?? fallbackTotals.fee_total ?? fallbackTotals.fees_total,
        rawFeeLines.reduce((sum, line) => sum + toNumber(line.amount), 0)
      ) - hiddenFeeTotal
    ) || roundMoney(feeLines.reduce((sum, line) => sum + toNumber(line.amount), 0))
  );
  const taxTotal = Math.max(
    0,
    roundMoney(
      toNumber(
        source.tax_total ?? source.taxes_total ?? fallbackTotals.tax_total ?? fallbackTotals.taxes_total,
        rawTaxLines.reduce((sum, line) => sum + toNumber(line.amount), 0)
      ) - hiddenTaxTotal
    ) || roundMoney(taxLines.reduce((sum, line) => sum + toNumber(line.amount), 0))
  );

  return {
    feeLines,
    taxLines,
    feeTotal,
    taxTotal,
    total: roundMoney(feeTotal + taxTotal),
  };
};

export const getTaxFeeLineItems = (breakdown) => {
  const normalized = normalizeTaxFeeBreakdown(breakdown);
  return [
    ...normalized.feeLines.map((line) => ({ label: line.label, value: line.amount, type: 'fee' })),
    ...normalized.taxLines.map((line) => ({ label: line.label, value: line.amount, type: 'tax' })),
  ];
};
